import { useMemo, useState } from "react";
import { Button, Skeleton } from "@mui/material";
import DefaultMessage from "components/Common/DefaultMessage/DefaultMessage.tsx";
import { useGetLaunchesQuery } from "features/api/launchApi.ts";
import { LaunchCard } from "./components/LaunchCard.tsx";
import {
	LaunchesGrid,
	ProductLaunchesPageContainer,
	ProductLaunchesPageContent,
	ProductLaunchesPageHeader
} from "./ProductLaunchesPage.styles.ts";
import * as Text from "./ProductLaunchesPage.text.ts";
import { sortLaunchesByEventStartDateDesc } from "./utils/ProductLaunchesPage.utils.ts";

export const LAUNCHES_PAGE_SIZE = 12;
const SKELETON_CARD_COUNT = 6;

export const ProductLaunchesPage = () => {
	const [limit, setLimit] = useState(LAUNCHES_PAGE_SIZE);
	const { data, isLoading, isFetching, isError } = useGetLaunchesQuery({ limit, offset: 0 });

	const items = data?.items;
	const launches = useMemo(() => sortLaunchesByEventStartDateDesc(items ?? []), [items]);
	// A short page means the API has nothing else to return, so no more requests are offered
	const hasMoreLaunches = (data?.totalRecords ?? 0) > launches.length && launches.length >= limit;

	const renderContent = () => {
		if (isLoading) {
			return (
				<LaunchesGrid data-testid="launches-skeleton">
					{Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
						<Skeleton
							key={index}
							variant="rectangular"
							height={280}
						/>
					))}
				</LaunchesGrid>
			);
		}

		if (isError) {
			return (
				<DefaultMessage
					dataTestId="default-message-launches-error"
					asset="/assets/NoOrdersSadBoxImage.svg"
					altMessage="Product launches unavailable"
					title={Text.LaunchesErrorText}
					subtitle={Text.LaunchesErrorSubText}
					removeHeight
				/>
			);
		}

		if (!launches.length) {
			return (
				<DefaultMessage
					dataTestId="default-message-no-launches"
					asset="/assets/NoOrdersSadBoxImage.svg"
					altMessage="No product launches available"
					title={Text.NoLaunchesText}
					subtitle={Text.NoLaunchesSubText}
					removeHeight
				/>
			);
		}

		return (
			<>
				<LaunchesGrid data-testid="launches-grid">
					{launches.map((launch, index) => (
						<LaunchCard
							key={`${launch.name ?? "launch"}-${index}`}
							launch={launch}
						/>
					))}
				</LaunchesGrid>
				{hasMoreLaunches && (
					<Button
						variant="outlined"
						color="primary"
						size="large"
						disabled={isFetching}
						onClick={() => setLimit((current) => current + LAUNCHES_PAGE_SIZE)}
					>
						{Text.LoadMoreText}
					</Button>
				)}
			</>
		);
	};

	return (
		<ProductLaunchesPageContainer>
			<ProductLaunchesPageHeader title={Text.ProductLaunchesTitleText} />
			<ProductLaunchesPageContent>{renderContent()}</ProductLaunchesPageContent>
		</ProductLaunchesPageContainer>
	);
};
